import { useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Icon, cn } from "@winche/ui";

/*
 * One table for the whole admin (audit 2026-10-10, admin-06): typed columns,
 * numbers right-aligned in tabular figures, a sticky header and first
 * column, row hover, sortable headers (aria-sort) and a footer computed per
 * column — no hand-written colSpan. Body 13 px, rows 36 px.
 */

export type SortDir = "asc" | "desc";

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** `right` for numbers: right-aligned, tabular figures. */
  align?: "left" | "right";
  cell: (row: T, index: number) => ReactNode;
  /** Makes the header a sort button. `null` values sort last. */
  sortValue?: (row: T) => number | string | null | undefined;
  /** Footer cell (totals); computed by the caller from the same rows. */
  footer?: ReactNode;
  className?: string;
  headerClassName?: string;
  /** Hint on the header (definition of the column). */
  title?: string;
}

function compare(a: number | string | null | undefined, b: number | string | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  defaultSort,
  emptyMessage = "No rows.",
  maxHeight,
  stickyFirstColumn = true,
  rowClassName,
  className,
}: {
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: (row: T, index: number) => string;
  /** Accessible name of the table (visually hidden). */
  caption: string;
  defaultSort?: { key: string; dir: SortDir };
  emptyMessage?: string;
  /** Scroll inside the table past this height (the header stays). */
  maxHeight?: number;
  stickyFirstColumn?: boolean;
  rowClassName?: (row: T) => string | undefined;
  className?: string;
}) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(defaultSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      // Nulls last in both directions.
      if (va == null || vb == null) return compare(va, vb);
      return sort.dir === "asc" ? compare(va, vb) : compare(vb, va);
    });
    return copy;
  }, [rows, columns, sort]);

  const hasFooter = columns.some((c) => c.footer != null);

  const toggleSort = (key: string, align: Column<T>["align"]) => {
    setSort((prev) => {
      if (prev?.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      // Numbers start high-to-low, text A→Z.
      return { key, dir: align === "right" ? "desc" : "asc" };
    });
  };

  const stickyCell = (i: number) =>
    stickyFirstColumn && i === 0 ? "sticky left-0 z-[1] bg-card group-hover:bg-hover" : undefined;

  return (
    <div
      className={cn("overflow-auto rounded-lg border border-border bg-card", className)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-separate border-spacing-0 text-label">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((col, i) => {
              const active = sort?.key === col.key;
              const ariaSort = active ? (sort!.dir === "asc" ? "ascending" : "descending") : col.sortValue ? "none" : undefined;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  title={col.title}
                  className={cn(
                    "sticky top-0 z-[2] h-9 whitespace-nowrap border-b border-border bg-card px-3 text-caption font-medium text-muted-foreground",
                    col.align === "right" ? "text-right" : "text-left",
                    stickyFirstColumn && i === 0 && "left-0 z-[3]",
                    col.headerClassName,
                  )}
                >
                  {col.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key, col.align)}
                      className={cn(
                        "relative inline-flex items-center gap-1 rounded-sm transition-colors duration-150 after:absolute after:-inset-x-1 after:-inset-y-2 hover:text-foreground",
                        active && "text-foreground",
                        col.align === "right" && "flex-row-reverse",
                      )}
                    >
                      {col.header}
                      <Icon
                        icon={active ? (sort!.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown}
                        size="xs"
                        className={active ? undefined : "opacity-50"}
                      />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sorted.map((row, r) => (
              <tr key={rowKey(row, r)} className={cn("group transition-colors duration-150 hover:bg-hover", rowClassName?.(row))}>
                {columns.map((col, i) => (
                  <td
                    key={col.key}
                    className={cn(
                      "h-9 border-b border-border px-3 py-1.5 align-middle group-last:border-b-0",
                      col.align === "right" ? "text-right tabular-nums" : "text-left",
                      stickyCell(i),
                      col.className,
                    )}
                  >
                    {col.cell(row, r)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {hasFooter && sorted.length > 0 ? (
          <tfoot>
            <tr>
              {columns.map((col, i) => (
                <td
                  key={col.key}
                  className={cn(
                    "h-9 border-t border-border bg-sunken px-3 font-medium",
                    col.align === "right" ? "text-right tabular-nums" : "text-left",
                    stickyFirstColumn && i === 0 && "sticky left-0 z-[1] bg-sunken",
                  )}
                >
                  {col.footer}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
